import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { BehaviorSubject, noop, Observable } from 'rxjs';
import { debounceTime, filter, tap } from 'rxjs/operators';
import {
  Select,
  Store,
  Actions,
  ofActionCompleted,
  ofActionDispatched,
  ofActionSuccessful,
  ofActionErrored,
} from '@ngxs/store';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { ReportLayout, ReportTypeQueryParamValues } from 'app/modules/reports/reports.types';
import { ObjectsManagerActions } from 'app/modules/settings/objects-manager/state/actions';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { ConfirmDialogComponent } from 'app/shared/components/widgets/dialog/confirm/confirm.component';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { CreateReportModel } from '../../reporting.types';
import { Report } from '../../state/actions';
import { LayoutsPage, ReportingState } from '../../state/state';
import { ReportingCreateModalComponent } from '../modals/create/create.component';
import { HttpErrorResponse } from '@angular/common/http';
import { typeTitleMap } from 'app/shared/models/type-title-map';
import { TsPaginatorChangeEvent } from 'tsui';
import { UntypedFormControl } from '@angular/forms';
import { NewPermission } from '@core/enums';

@Component({
  selector: 'app-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportingListComponent extends OnDestroyMixin implements OnInit {
  @Select(ReportingState.getLayoutsPage) layoutsPage$: Observable<LayoutsPage>;
  @Select(ReportingState.getSelectedLayout) selectedLayout$: Observable<ReportLayout<any>>;
  @Select(ObjectsManagerState.get) customObjects$: Observable<CustomObject[]>;

  searchInputControl: UntypedFormControl;

  protected readonly NewPermission = NewPermission;
  readonly validReportTypes = [ReportTypeQueryParamValues.SingleObject, ReportTypeQueryParamValues.TimeSeries];
  readonly loadingReportLayouts = new BehaviorSubject(false);
  displayColumns = ['fav', 'name', 'relatedTo', 'user_id', 'created_at', 'actions'];

  constructor(
    private readonly router: Router,
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly matDialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly activatedRoute: ActivatedRoute,
  ) {
    super();
  }

  ngOnInit(): void {
    this.searchInputControl = new UntypedFormControl();
    this.setupReportLayoutActionListeners();

    this.store.dispatch(new ObjectsManagerActions.Get());
    this.handleReportDeleteError();

    this.selectedLayout$
      .pipe(
        untilComponentDestroyed(this),
        filter((layout) => !!layout),
        tap((layout) => this.router.navigate(['reporting', 'builder', layout.id])),
      )
      .subscribe(noop);

    this.searchInputControl.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        debounceTime(500),
        filter((query) => query),
        tap((query) => this.dispatchGetReports(this.activatedRoute.snapshot.queryParams, query)),
      )
      .subscribe(noop);

    this.activatedRoute.queryParams
      .pipe(
        untilComponentDestroyed(this),
        tap(() => this.searchInputControl.setValue('')),
        tap((queryParams) => this.dispatchGetReports(queryParams)),
      )
      .subscribe(noop);
  }

  private dispatchGetReports(queryParams: any, query: string = null): void {
    const args: any = { report_types: this.validReportTypes };

    if (query) {
      args.query = query;
    }

    if (queryParams.byme) {
      args.created_by_me = true;
    } else if (queryParams.favorites) {
      args.favorites = true;
    }

    this.store.dispatch(new Report.Get(args));
  }

  create(): void {
    this.matDialog
      .open(ReportingCreateModalComponent, {
        autoFocus: false,
      })
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        filter((report) => !!report),
        tap((report: CreateReportModel) => this.store.dispatch(new Report.Create(report))),
      )
      .subscribe(noop);
  }

  delete(report: ReportLayout<any>): void {
    this.matDialog
      .open(ConfirmDialogComponent, {
        autoFocus: false,
        data: {
          title: 'Confirmation',
          message: `Are you sure you want to delete report "${report.name}"?`,
        },
      })
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        filter((result: boolean) => result),
        tap(() => this.store.dispatch(new Report.Delete(report.id))),
      )
      .subscribe(noop);
  }

  getRelatedTo(definition: string, customObjects: CustomObject[]): string {
    if (!definition) {
      return null;
    }

    const { related_to_type: relatedToType } = JSON.parse(definition);

    return (
      typeTitleMap[relatedToType] || customObjects.find(({ name }) => name === relatedToType)?.label || relatedToType
    );
  }

  duplicate(model: ReportLayout<any>): void {
    this.matDialog
      .open(ConfirmDialogComponent, {
        autoFocus: false,
        data: {
          title: 'Confirmation',
          message: `Are you sure you want to duplicate report "${model.name}"?`,
        },
      })
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        filter((result: boolean) => result),
        tap(() => this.store.dispatch(new Report.Duplicate(model))),
      )
      .subscribe(noop);
  }

  onPageChange($event: TsPaginatorChangeEvent) {
    this.store.dispatch(
      new Report.Get({
        page: $event.activePage,
        per_page: $event.pageSize,
        report_types: this.validReportTypes,
      }),
    );
  }

  toggleFavorite(id: number): void {
    this.store.dispatch(new Report.ToggleFavorite(id));
  }

  private handleReportDeleteError(): void {
    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionCompleted(Report.Delete),
        tap(({ result: { error } }) => {
          if (error instanceof HttpErrorResponse) {
            const errorMessage = error.error.report_layout.errors;
            this.snackBar.open(errorMessage, 'Dismiss', { duration: 5000, panelClass: 'error-snackbar' });
          }
        }),
      )
      .subscribe(noop);
  }

  private setupReportLayoutActionListeners() {
    this.actions$.pipe(ofActionDispatched(Report.Get), untilComponentDestroyed(this)).subscribe(() => {
      this.loadingReportLayouts.next(true);
    });

    this.actions$.pipe(ofActionSuccessful(Report.Get), untilComponentDestroyed(this)).subscribe(() => {
      this.loadingReportLayouts.next(false);
    });

    this.actions$.pipe(ofActionErrored(Report.Get), untilComponentDestroyed(this)).subscribe(() => {
      this.loadingReportLayouts.next(false);
      this.snackBar.open('Failed to load reports', 'Dismiss', { duration: 5000, panelClass: 'error-snackbar' });
    });
  }
}
